/**
 * El índice del manual: qué pasos hay, en qué orden y con qué imagen.
 *
 * Esto es ESTRUCTURA, no traducción, así que vive aquí y no en los catálogos.
 * Los títulos y los cuerpos salen de `manual-es/en/de.ts` con la clave
 * derivada del `id`: `of_apartamentos_titulo`, `of_apartamentos_cuerpo`.
 *
 * Lo comparten tres cosas, y ahí está la gracia:
 *
 *   · el manual del panel, que enseña las dos mitades —quien está en la
 *     oficina atiende el teléfono de quien está en un piso, así que necesita
 *     ver la pantalla que la otra tiene delante—;
 *   · el manual del móvil, que enseña solo la suya;
 *   · y `tools/manual-capturas.mjs`, que saca las imágenes conduciendo las
 *     aplicaciones de verdad.
 *
 * Una sola lista para los tres significa que no puede haber un paso sin
 * imagen ni una imagen sin paso. `npm run manual:check` lo comprueba
 * ejecutandolo sobre los ficheros: **un manual con una imagen rota es peor
 * que no tener manual**, porque el cliente lo abre una vez y decide si esto
 * esta terminado.
 *
 * ### Las imágenes son capturas de VERDAD, no dibujos
 *
 * Salen de la aplicación corriendo, con los datos de demostración, una vez
 * por idioma. Un manual con la interfaz dibujada a mano envejece sin que nadie
 * se entere; uno con capturas envejece igual, pero se vuelve a generar con un
 * comando y se ve en el `git diff` que cambió.
 */

/** Un paso del manual. `imagen` es el nombre del fichero, sin idioma ni extensión. */
export interface PasoManual {
    readonly id: string;
    readonly imagen: string;
}

/**
 * Lo que hace la oficina, en el orden en el que hay que hacerlo la primera vez.
 *
 * No es el orden del menú: es el orden de montaje. Sin apartamentos no hay
 * nada que asignar, sin recorrido no hay nada que grabar y sin personal no hay
 * quien grabe. Un manual ordenado como el menú deja al cliente empezando por
 * la pantalla que todavía no puede usar.
 */
export const PASOS_OFICINA: readonly PasoManual[] = [
    { id: 'apartamentos', imagen: 'oficina-apartamentos' },
    { id: 'recorrido', imagen: 'oficina-recorrido' },
    { id: 'qr', imagen: 'oficina-qr' },
    { id: 'personal', imagen: 'oficina-personal' },
    { id: 'reparto', imagen: 'oficina-reparto' },
    { id: 'limpiezas', imagen: 'oficina-limpiezas' },
    { id: 'detalle', imagen: 'oficina-detalle' },
    { id: 'enlace', imagen: 'oficina-enlace' },
    { id: 'incidencias', imagen: 'oficina-incidencias' },
    { id: 'informes', imagen: 'oficina-informes' },
    { id: 'ajustes', imagen: 'oficina-ajustes' },
] as const;

/** Lo que hace quien limpia, en el orden de una jornada. */
export const PASOS_MOVIL: readonly PasoManual[] = [
    { id: 'alta', imagen: 'movil-alta' },
    { id: 'dia', imagen: 'movil-dia' },
    { id: 'recorrido', imagen: 'movil-recorrido' },
    { id: 'grabar', imagen: 'movil-grabar' },
    { id: 'resumen', imagen: 'movil-resumen' },
    { id: 'cola', imagen: 'movil-cola' },
] as const;

/**
 * La clave del catálogo para el título o el cuerpo de un paso.
 *
 * Los dos prefijos van escritos ENTEROS, con un `match`, y no como
 * `` `${familia}_${paso.id}_${que}` ``. La razón es `i18n:check`: para no dar
 * por muertas las familias construidas con plantilla, busca el prefijo literal
 * que hay ANTES del primer `${`. Con la plantilla empezando por `${` no hay
 * prefijo que encontrar, y las cuarenta y tantas claves del manual saldrían
 * como «sin ningún consumidor».
 *
 * O sea que escribir `of_` y `mo_` a mano no es verbosidad: es lo que hace que
 * el chequeo pueda ver estas claves. Es el mismo razonamiento por el que
 * `Marca::leer()` nombra sus claves enteras en vez de interpolar el idioma.
 */
export function claveDelPaso(
    familia: 'of' | 'mo',
    paso: PasoManual,
    que: 'titulo' | 'cuerpo'
): string {
    return familia === 'of' ? `of_${paso.id}_${que}` : `mo_${paso.id}_${que}`;
}

/**
 * La ruta de la imagen de un paso, en un idioma.
 *
 * Las capturas van por idioma —`manual/de/movil-dia.png`— y no en uno solo:
 * un manual en alemán con la pantalla en español no explica nada, y además es
 * justo el sitio donde el cliente comprueba si el producto habla su idioma de
 * verdad o solo por encima.
 *
 * `base` es `/manual/` en la app del personal y `/admin/manual/` en el panel,
 * porque cada una sirve las suyas desde su propio `dist/`. Podrían compartirse
 * —son los mismos ficheros para el móvil— y sería peor: el panel dejaría de
 * funcionar si algún día cambia la ruta de la otra aplicación.
 */
export function imagenDelPaso(base: string, idioma: string, paso: PasoManual): string {
    // `.png` y no WebP: pesaria la mitad, pero convertirlo pide una dependencia
    // que este repositorio no tiene. Ver el encabezado de
    // `tools/manual-capturas.mjs`.
    return `${base}${idioma}/${paso.imagen}.png`;
}
